"use client";

// Hadiah pembelian di keranjang (mig 8680; rencana §6.3): memuat aturan toko
// untuk produk di keranjang (+ sisa kuota, mig 8740) dan menghitung kartu
// "Hadiah untukmu" setiap isi keranjang berubah. Pilihan varian hadiah
// tersimpan bersama keranjang (`useCart().giftChoices`, D3) — tak hilang saat
// halaman dimuat ulang. Hadiah tak bisa dilewati (H40, mig 8860). Pembeli yang
// login: sisa pesanan sebelumnya ikut dihitung (`my_gift_carry`, H55, mig
// 8880), begitu pula pembelian lama yang masih bisa bertambah
// (`my_gift_open_purchases`, Hadiah Tahap 6-2). Hitungannya murni di
// `src/lib/giftCart.ts`.

import { useCallback, useEffect, useMemo, useState } from "react";

import type { GiftClaimLine } from "@/lib/gift";
import {
  EMPTY_GIFT_DATA,
  cartGiftEntries,
  cartGiftPayload,
  giftOpenByRule,
  type GiftCarry,
  type GiftCartEntry,
  type GiftCartProduct,
  type GiftOpenPurchase,
  type StorefrontGiftData
} from "@/lib/giftCart";
import { useCart } from "@/lib/cart";
import { fetchMyGiftCarry, fetchMyGiftOpenPurchases, fetchStorefrontGiftData } from "@/lib/supabase/gifts";

export type CartGifts = {
  entries: GiftCartEntry[];
  /** Muatan `p_gifts` dari kartu yang siap; [] = checkout tanpa hadiah. */
  payload: ReturnType<typeof cartGiftPayload>;
  /** Aturan hadiah untuk isi keranjang SAAT INI belum termuat. */
  pending: boolean;
  /** Gagal memuat aturan hadiah. */
  failed: boolean;
  setClaim: (ruleId: string, lines: GiftClaimLine[]) => void;
  reload: () => void;
};

export function useCartGifts(input: {
  carts: readonly GiftCartProduct[];
  /** Slot level pembeli (`bundleCustomerSlot`). */
  slot: number | null;
  /** Pelanggan yang login; null = tamu (tanpa sisa pesanan sebelumnya). */
  customerId: string | null;
  enabled: boolean;
}): CartGifts {
  const { carts, slot, customerId, enabled } = input;
  // Kunci efek berupa STRING (idiom kuota PO di CartCheckout): `carts` berganti
  // identitas tiap ketikan catatan; isi yang relevan hanya produk & varian.
  const productKey = useMemo(() => [...new Set(carts.map((c) => c.productId))].sort().join(","), [carts]);
  const variantKey = useMemo(
    () => [...new Set(carts.flatMap((c) => c.lines.map((l) => l.variationId)))].sort().join(","),
    [carts]
  );
  const [tick, setTick] = useState(0);
  const loadKey = `${enabled ? 1 : 0}|${productKey}|${variantKey}|${customerId ?? ""}|${tick}`;
  const [state, setState] = useState<{
    key: string;
    data: StorefrontGiftData;
    carry: ReadonlyMap<string, GiftCarry>;
    open: ReadonlyMap<string, readonly GiftOpenPurchase[]>;
    failed: boolean;
    now: Date;
  } | null>(null);
  const { giftChoices, setGiftClaim } = useCart();
  const claims = useMemo(() => new Map(Object.entries(giftChoices.claims)), [giftChoices.claims]);

  useEffect(() => {
    if (!enabled || !productKey) {
      setState({ key: loadKey, data: EMPTY_GIFT_DATA, carry: new Map(), open: new Map(), failed: false, now: new Date() });
      return;
    }
    let alive = true;
    fetchStorefrontGiftData(productKey.split(","), { cartVariantIds: variantKey ? variantKey.split(",") : [] })
      .then(async (data) => {
        // Sisa pesanan sebelumnya (H55): gagal dimuat = dihitung tanpa sisa —
        // server tetap menggabungnya sesudah checkout, kartu hanya kurang tahu.
        const ruleIds = data.rules.map((r) => r.id);
        const [carry, open] =
          customerId && ruleIds.length
            ? await Promise.all([
                fetchMyGiftCarry(ruleIds).catch(() => new Map<string, GiftCarry>()),
                // Pembelian lama (Tahap 6-2): gagal dimuat = kartu seperti
                // sebelumnya (bisa kurang dari yang didapat, tak pernah lebih).
                fetchMyGiftOpenPurchases(ruleIds)
                  .then((list) => giftOpenByRule(list))
                  .catch(() => new Map<string, GiftOpenPurchase[]>())
              ])
            : [new Map<string, GiftCarry>(), new Map<string, GiftOpenPurchase[]>()];
        if (alive) setState({ key: loadKey, data, carry, open, failed: false, now: new Date() });
      })
      .catch(() => {
        if (alive) {
          setState({ key: loadKey, data: EMPTY_GIFT_DATA, carry: new Map(), open: new Map(), failed: true, now: new Date() });
        }
      });
    return () => {
      alive = false;
    };
  }, [enabled, productKey, variantKey, customerId, loadKey]);

  const entries = useMemo(
    () =>
      state
        ? cartGiftEntries({ data: state.data, carts, slot, now: state.now, claims, carry: state.carry, open: state.open })
        : [],
    [state, carts, slot, claims]
  );
  const payload = useMemo(() => cartGiftPayload(entries), [entries]);

  const setClaim = useCallback(
    (ruleId: string, lines: GiftClaimLine[]) => setGiftClaim(ruleId, lines),
    [setGiftClaim]
  );
  const reload = useCallback(() => setTick((t) => t + 1), []);

  return {
    entries,
    payload,
    pending: enabled && Boolean(productKey) && state?.key !== loadKey,
    failed: Boolean(state?.failed) && state?.key === loadKey,
    setClaim,
    reload
  };
}
